import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, CheckCircle, Loader2, AlertCircle, ShieldCheck, TrendingUp, Zap, Globe, MapPin, Star, ChevronDown } from "lucide-react";
import { fadeUp, LuxuryBackground, Section, Card, GoldButton, PageHero } from "../components/shared/UIComponents";
import { API } from "../data/constants";
import { usePageNavigate } from "../hooks/usePageNavigate";

const LOAN_TYPES = [
  { value: "fix_flip", label: "Fix & Flip" },
  { value: "bridge", label: "Bridge" },
  { value: "dscr", label: "DSCR" },
  { value: "cash_out", label: "Cash-Out Refi" },
  { value: "construction", label: "Construction" },
  { value: "ground_up", label: "Ground Up" },
  { value: "multifamily", label: "Multifamily" },
  { value: "commercial", label: "Commercial" },
];
const COMPANY_TYPES = [
  { value: "bank", label: "Bank" },
  { value: "credit_union", label: "Credit Union" },
  { value: "private_fund", label: "Private Fund" },
  { value: "hedge_fund", label: "Hedge Fund" },
  { value: "family_office", label: "Family Office" },
  { value: "mortgage_company", label: "Mortgage Company" },
  { value: "other", label: "Other" },
];

const WHY_LENDERS = [
  { icon: ShieldCheck, title: "Qualified Deal Flow", text: "Access borrower scenarios submitted by real estate investors seeking acquisition, renovation, bridge, and rental financing." },
  { icon: TrendingUp, title: "Structured Loan Packages", text: "Each deal is organized with borrower context, property details, and leverage metrics to simplify underwriting review." },
  { icon: Zap, title: "Efficient Deal Review", text: "Reduce wasted time reviewing incomplete submissions with structured deal packaging." },
  { icon: Globe, title: "Marketplace Distribution", text: "Expand your origination pipeline through a digital capital marketplace built for real estate investors." },
];

const HOW_IT_WORKS = [
  { title: "Set Your Lending Criteria", text: "Define your lending box including asset types, leverage limits, loan sizes, and preferred markets." },
  { title: "Receive Matched Opportunities", text: "Borrower scenarios are routed to lenders whose criteria align with the deal." },
  { title: "Review Structured Packages", text: "Access borrower summaries, leverage metrics, and deal context in an organized package." },
  { title: "Submit Terms", text: "Provide indicative loan terms and engage borrowers directly through the platform." },
  { title: "Move Toward Closing", text: "Work with borrowers through underwriting, documentation, and funding coordination." },
];

const LENDER_TYPES = [
  "Hard Money Lenders",
  "Bridge Lenders",
  "DSCR Rental Lenders",
  "Private Lending Funds",
  "Debt Funds",
  "Family Office Capital",
  "Institutional Credit Partners",
];

const inputCls = "w-full rounded-xl border border-cream-warm bg-white px-4 py-3 text-sm text-[#0A0A0A] placeholder:text-[#999] focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400/30 transition-colors";
const selectCls = inputCls;
const labelCls = "block text-xs font-medium text-[#555] mb-1.5 uppercase tracking-wider";

export default function LenderOnboardPage() {
  const { navigateTo } = usePageNavigate();
  const howItWorksRef = useRef(null);
  const [formStep, setFormStep] = useState(1);
  const [form, setForm] = useState({
    first_name: "", last_name: "", lender_name: "", email: "", phone: "",
    company_type: "private_fund", loan_types: [], states_served: [],
    min_fico: "", max_ltv: "", max_ltc: "", min_dscr: "",
    min_loan_amount: "", max_loan_amount: "", turn_time_days: "",
    broker_friendly: true, notes: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleLoanType = (val) => set("loan_types", form.loan_types.includes(val) ? form.loan_types.filter(t => t !== val) : [...form.loan_types, val]);

  const handleStep1 = (e) => {
    e.preventDefault();
    setFormStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const payload = {
        lender_name: form.lender_name,
        contact_name: `${form.first_name} ${form.last_name}`.trim(),
        email: form.email, phone: form.phone || null,
        company_type: form.company_type, loan_types: form.loan_types,
        states_served: form.states_served.length ? form.states_served : ["TX"],
        min_fico: form.min_fico ? parseInt(form.min_fico) : null,
        max_ltv: form.max_ltv ? parseFloat(form.max_ltv) / 100 : null,
        max_ltc: form.max_ltc ? parseFloat(form.max_ltc) / 100 : null,
        min_dscr: form.min_dscr ? parseFloat(form.min_dscr) : null,
        min_loan_amount: form.min_loan_amount ? parseFloat(form.min_loan_amount) : null,
        max_loan_amount: form.max_loan_amount ? parseFloat(form.max_loan_amount) : null,
        turn_time_days: form.turn_time_days ? parseInt(form.turn_time_days) : null,
        broker_friendly: form.broker_friendly, notes: form.notes || null,
      };
      await axios.post(`${API}/lenders/onboard`, payload);
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) setError(detail.map(d => d.msg || JSON.stringify(d)).join("; "));
      else setError(typeof detail === "string" ? detail : "Submission failed. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="relative" data-testid="lender-onboard-page">
      <PageHero eyebrow="For Lenders" title="Join the Imaani Capital Lender Network"
        description="Access structured real estate investment opportunities from a growing pipeline of borrower submissions across high-growth markets." />
      <div className="relative">
        <LuxuryBackground />

        {/* Early CTA */}
        <section className="relative z-10 mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-16 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white p-6">
            <p className="text-base font-medium text-[#333]">Ready to define your lending criteria and start receiving matched deals?</p>
            <GoldButton onClick={() => document.getElementById("lender-form")?.scrollIntoView({ behavior: "smooth", block: "start" })} className="shrink-0 !text-xs !px-5 !py-2" data-testid="lender-early-cta">
              Join the Lender Network <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </GoldButton>
          </motion.div>
        </section>

        {/* Why Lenders Join */}
        <Section eyebrow="Why Join" title="Why Lenders Work With Imaani Capital"
          description="Imaani Capital provides lenders with a marketplace channel for real estate investment opportunities. Borrower scenarios are structured with deal context and organized documentation to streamline underwriting review.">
          <div className="grid gap-5 sm:grid-cols-2">
            {WHY_LENDERS.map((vp, i) => {
              const Icon = vp.icon;
              return (
                <motion.div key={vp.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                  <Card className="h-full">
                    <div className="w-fit rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-50 p-3 border border-emerald-200 mb-4">
                      <Icon className="h-5 w-5 text-emerald-700" />
                    </div>
                    <h3 className="text-base font-bold text-[#0A0A0A] mb-2">{vp.title}</h3>
                    <p className="text-sm text-[#555] leading-relaxed">{vp.text}</p>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </Section>

        {/* Lender Testimonial */}
        <section className="relative z-10 mx-auto max-w-7xl px-4 pb-10 sm:px-6 sm:pb-16 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Card hover={false} className="!bg-gradient-to-br !from-emerald-50 !to-white !border-emerald-200">
              <div className="flex flex-col md:flex-row md:items-start gap-6">
                <div className="flex items-center gap-4 md:flex-col md:items-center md:text-center md:min-w-[140px]">
                  <div className="h-14 w-14 rounded-full bg-emerald-700 flex items-center justify-center text-xl font-bold text-white shrink-0">JT</div>
                  <div>
                    <div className="text-sm font-bold text-[#0A0A0A]">James Thornton</div>
                    <div className="text-xs text-[#888]">Private Lender — Dallas, TX</div>
                    <div className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 border border-emerald-200 text-[10px] font-bold text-emerald-700">
                      <CheckCircle className="h-3 w-3" /> $1.8M funded
                    </div>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex gap-0.5 mb-3">
                    {[1,2,3,4,5].map(s => <Star key={s} className="h-4 w-4 text-gold fill-gold" />)}
                  </div>
                  <blockquote className="text-base leading-relaxed text-[#333] italic" data-testid="lender-testimonial-quote">
                    "The deal packages come pre-structured with underwriting context and risk flags. I can evaluate a deal in 15 minutes instead of spending hours chasing documents."
                  </blockquote>
                </div>
              </div>
            </Card>
          </motion.div>
        </section>

        {/* How It Works for Lenders */}
        <div ref={howItWorksRef} id="how-it-works">
          <Section eyebrow="Process" title="How the Lender Network Works">
            <div className="grid gap-5 lg:grid-cols-2">
              {HOW_IT_WORKS.map((step, i) => (
                <motion.div key={step.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                  <Card className="flex gap-5 items-start">
                    <motion.div whileHover={{ rotate: 360, scale: 1.1 }} transition={{ duration: 0.5 }}
                      className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-700 to-emerald-900 border-2 border-gold/50 text-lg font-bold text-white">0{i + 1}</motion.div>
                    <div><h3 className="text-xl font-bold text-[#0A0A0A]">{step.title}</h3><p className="mt-3 text-base leading-7 text-[#333]">{step.text}</p></div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </Section>
        </div>

        {/* Lender Types */}
        <Section eyebrow="Network" title="Who Joins the Lender Network"
          description="Our platform works with a diverse range of capital providers focused on real estate lending.">
          <Card hover={false}>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {LENDER_TYPES.map((item, i) => (
                <motion.div key={item} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                  className="rounded-xl border border-cream-warm bg-cream p-4 text-[#333] hover:border-emerald-200 hover:shadow-sm transition-all flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-emerald-600 shrink-0" />
                  <span className="text-sm font-medium">{item}</span>
                </motion.div>
              ))}
            </div>
          </Card>
        </Section>

        {/* Markets — Texas Only */}
        <section className="relative z-10 mx-auto max-w-7xl px-4 pb-12 sm:px-6 sm:pb-20 lg:px-8">
          <motion.div {...fadeUp} className="relative rounded-[3rem] overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#022c22] to-[#03352a] rounded-[3rem]" />
            <div className="relative p-10 md:p-16">
              <div className="grid gap-12 lg:grid-cols-[1fr_1fr] lg:items-center">
                <div>
                  <p className="text-sm uppercase tracking-[0.4em] text-gold/70">Market Focus</p>
                  <h2 className="mt-4 text-3xl font-semibold tracking-tight text-cream md:text-5xl luxury-text-gradient">Markets We Support</h2>
                  <p className="mt-6 max-w-2xl text-lg leading-8 text-cream-soft">
                    Imaani Capital connects lenders with real estate investors operating in the Texas market.
                  </p>
                </div>
                <div className="flex flex-col items-center gap-5">
                  <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
                    whileHover={{ scale: 1.03, y: -5 }}
                    className="w-full rounded-2xl border-2 border-gold/50 bg-gradient-to-br from-gold/15 to-emerald-500/10 p-8 backdrop-blur-sm text-center" data-testid="market-texas-card">
                    <MapPin className="h-8 w-8 text-gold mx-auto mb-3" />
                    <div className="text-2xl font-bold text-cream">Texas</div>
                    <div className="mt-2 flex items-center justify-center gap-3 flex-wrap">
                      {["DFW", "Houston", "Austin", "San Antonio"].map(city => (
                        <span key={city} className="text-xs font-medium text-cream/60 bg-cream/10 px-3 py-1 rounded-full">{city}</span>
                      ))}
                    </div>
                  </motion.div>
                  <p className="text-sm text-cream/50 text-center leading-relaxed max-w-md" data-testid="market-expansion-note">
                    Currently accepting lenders for the Texas market (DFW, Houston, Austin). Additional markets launching 2026. Join now for early access.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Registration Form — Two-Step */}
        <section className="relative z-10 mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-16 lg:px-8">
          <div id="lender-form" className="max-w-3xl mx-auto">
            <Card hover={false}>
              {submitted ? (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                  className="text-center py-12 space-y-5" data-testid="lender-onboard-success">
                  <div className="mx-auto h-16 w-16 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                    <CheckCircle className="h-8 w-8 text-emerald-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#0A0A0A]">Application Received</h3>
                  <p className="text-sm text-[#555] max-w-md mx-auto leading-relaxed">
                    Thank you for your interest in joining the Imaani Capital lending network. Our team will review your application and reach out within 24-48 hours.
                  </p>
                  <GoldButton onClick={() => navigateTo("home")} data-testid="lender-onboard-home-btn">
                    Back to Home
                  </GoldButton>
                </motion.div>
              ) : (
                <>
                  {/* Progress Indicator */}
                  <div className="flex items-center gap-3 mb-8" data-testid="form-progress-indicator">
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${formStep >= 1 ? "bg-emerald-100 text-emerald-700 border border-emerald-200" : "bg-gray-100 text-gray-400 border border-gray-200"}`}>
                      <span className="h-5 w-5 rounded-full bg-emerald-700 text-white flex items-center justify-center text-[10px]">1</span>
                      Contact Info
                    </div>
                    <div className="flex-1 h-px bg-cream-warm" />
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${formStep >= 2 ? "bg-emerald-100 text-emerald-700 border border-emerald-200" : "bg-gray-100 text-gray-400 border border-gray-200"}`}>
                      <span className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] ${formStep >= 2 ? "bg-emerald-700 text-white" : "bg-gray-300 text-white"}`}>2</span>
                      Lending Criteria
                    </div>
                  </div>

                  {formStep === 1 ? (
                    <motion.div key="step1" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}>
                      <div className="mb-8">
                        <h3 className="text-2xl font-bold text-[#0A0A0A]">Lender Registration</h3>
                        <p className="text-sm text-[#555] mt-2">Tell us about yourself. Step 1 of 2.</p>
                      </div>
                      <form onSubmit={handleStep1} className="space-y-6" data-testid="lender-onboard-form-step1">
                        <div className="grid gap-4 sm:grid-cols-2">
                          <label className="block">
                            <span className={labelCls}>First Name *</span>
                            <input required value={form.first_name} onChange={e => set("first_name", e.target.value)}
                              className={inputCls} placeholder="James" data-testid="onboard-first-name" />
                          </label>
                          <label className="block">
                            <span className={labelCls}>Last Name *</span>
                            <input required value={form.last_name} onChange={e => set("last_name", e.target.value)}
                              className={inputCls} placeholder="Thornton" data-testid="onboard-last-name" />
                          </label>
                          <label className="block sm:col-span-2">
                            <span className={labelCls}>Company / Fund Name *</span>
                            <input required value={form.lender_name} onChange={e => set("lender_name", e.target.value)}
                              className={inputCls} placeholder="Your firm name" data-testid="onboard-lender-name" />
                          </label>
                          <label className="block">
                            <span className={labelCls}>Email *</span>
                            <input required type="email" value={form.email} onChange={e => set("email", e.target.value)}
                              className={inputCls} placeholder="deals@yourfirm.com" data-testid="onboard-email" />
                          </label>
                          <label className="block">
                            <span className={labelCls}>Phone</span>
                            <input value={form.phone} onChange={e => set("phone", e.target.value)}
                              className={inputCls} placeholder="(555) 000-0000" data-testid="onboard-phone" />
                          </label>
                        </div>
                        <GoldButton type="submit" className="w-full !rounded-xl" data-testid="onboard-step1-btn">
                          <span className="flex items-center justify-center gap-2">Get Early Access <ArrowRight className="h-4 w-4" /></span>
                        </GoldButton>
                      </form>
                    </motion.div>
                  ) : (
                    <motion.div key="step2" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}>
                      <div className="mb-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-2xl font-bold text-[#0A0A0A]">Lending Criteria</h3>
                            <p className="text-sm text-[#555] mt-2">Define your lending box. Step 2 of 2.</p>
                          </div>
                          <button type="button" onClick={() => setFormStep(1)} className="text-xs text-emerald-700 font-medium hover:underline" data-testid="onboard-back-step1">
                            Back to Step 1
                          </button>
                        </div>
                        <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-200 p-3 flex items-center gap-3">
                          <CheckCircle className="h-4 w-4 text-emerald-600 shrink-0" />
                          <span className="text-xs text-emerald-700"><strong>{form.first_name} {form.last_name}</strong> — {form.email}</span>
                        </div>
                      </div>
                      <form onSubmit={handleSubmit} className="space-y-6" data-testid="lender-onboard-form-step2">
                        <div>
                          <label className="block mb-4">
                            <span className={labelCls}>Company Type *</span>
                            <select required value={form.company_type} onChange={e => set("company_type", e.target.value)}
                              className={selectCls} data-testid="onboard-company-type">
                              {COMPANY_TYPES.map(ct => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
                            </select>
                          </label>
                          <div className="mb-4">
                            <span className={labelCls}>Loan Types</span>
                            <div className="flex gap-2 flex-wrap mt-1">
                              {LOAN_TYPES.map(lt => (
                                <button key={lt.value} type="button" onClick={() => toggleLoanType(lt.value)} data-testid={`onboard-lt-${lt.value}`}
                                  className={`px-4 py-2 rounded-xl border text-sm font-medium transition-all ${
                                    form.loan_types.includes(lt.value)
                                      ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                                      : "border-cream-warm bg-white text-[#555] hover:border-emerald-300"
                                  }`}>{lt.label}</button>
                              ))}
                            </div>
                          </div>
                          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            <label className="block">
                              <span className={labelCls}>Min FICO</span>
                              <input type="number" value={form.min_fico} onChange={e => set("min_fico", e.target.value)}
                                className={inputCls} placeholder="660" data-testid="onboard-min-fico" />
                            </label>
                            <label className="block">
                              <span className={labelCls}>Max LTV (%)</span>
                              <input type="number" value={form.max_ltv} onChange={e => set("max_ltv", e.target.value)}
                                className={inputCls} placeholder="75" data-testid="onboard-max-ltv" />
                            </label>
                            <label className="block">
                              <span className={labelCls}>Max LTC (%)</span>
                              <input type="number" value={form.max_ltc} onChange={e => set("max_ltc", e.target.value)}
                                className={inputCls} placeholder="90" data-testid="onboard-max-ltc" />
                            </label>
                            <label className="block">
                              <span className={labelCls}>Min DSCR</span>
                              <input type="number" step="0.01" value={form.min_dscr} onChange={e => set("min_dscr", e.target.value)}
                                className={inputCls} placeholder="1.0" data-testid="onboard-min-dscr" />
                            </label>
                            <label className="block">
                              <span className={labelCls}>Min Loan Size ($)</span>
                              <input type="number" value={form.min_loan_amount} onChange={e => set("min_loan_amount", e.target.value)}
                                className={inputCls} placeholder="100000" data-testid="onboard-min-loan" />
                            </label>
                            <label className="block">
                              <span className={labelCls}>Max Loan Size ($)</span>
                              <input type="number" value={form.max_loan_amount} onChange={e => set("max_loan_amount", e.target.value)}
                                className={inputCls} placeholder="5000000" data-testid="onboard-max-loan" />
                            </label>
                            <label className="block">
                              <span className={labelCls}>Turn Time (days)</span>
                              <input type="number" value={form.turn_time_days} onChange={e => set("turn_time_days", e.target.value)}
                                className={inputCls} placeholder="10" data-testid="onboard-turn-time" />
                            </label>
                            <label className="flex items-center gap-3 self-end pb-3">
                              <input type="checkbox" checked={form.broker_friendly} onChange={e => set("broker_friendly", e.target.checked)}
                                className="h-4 w-4 rounded border-cream-warm text-emerald-600 accent-emerald-600" data-testid="onboard-broker-friendly" />
                              <span className="text-sm text-[#555]">Broker-Friendly</span>
                            </label>
                          </div>
                        </div>
                        <label className="block">
                          <span className={labelCls}>Additional Notes</span>
                          <textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={3}
                            className={inputCls + " resize-none"} placeholder="Tell us about your lending preferences, niche, or anything else..." data-testid="onboard-notes" />
                        </label>
                        {error && (
                          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-600 flex items-center gap-2">
                            <AlertCircle className="h-5 w-5 shrink-0" /> {error}
                          </div>
                        )}
                        <GoldButton type="submit" disabled={loading} className="w-full !rounded-xl" data-testid="onboard-submit-btn">
                          {loading ? <span className="flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Submitting...</span>
                            : <span className="flex items-center justify-center gap-2">Submit My Lending Criteria <ArrowRight className="h-4 w-4" /></span>}
                        </GoldButton>
                      </form>
                    </motion.div>
                  )}
                </>
              )}
            </Card>
          </div>
        </section>

        {/* Final CTA */}
        <section className="relative z-10 mx-auto max-w-7xl px-4 pb-16 sm:px-6 sm:pb-28 lg:px-8">
          <motion.div {...fadeUp} className="relative rounded-[3rem] overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-gold/40 via-emerald-500/25 to-gold/30 p-[2px] rounded-[3rem]">
              <div className="absolute inset-[2px] rounded-[calc(3rem-2px)] bg-gradient-to-br from-[#03352a] via-[#022c22] to-[#044332]" />
            </div>
            <div className="relative p-10 md:p-16 text-center">
              <p className="text-sm uppercase tracking-[0.4em] text-gold/70 mb-4">Get Started</p>
              <h2 className="text-3xl font-semibold tracking-tight text-cream md:text-5xl luxury-text-gradient">See How the Process Works</h2>
              <p className="mt-6 max-w-2xl mx-auto text-lg leading-8 text-cream-soft">
                Learn how the Imaani Capital lender network delivers structured deal flow to your inbox.
              </p>
              <div className="mt-10">
                <GoldButton onClick={() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth", block: "start" })} data-testid="lender-final-cta">
                  View the Process <ArrowRight className="h-4 w-4 ml-1" />
                </GoldButton>
              </div>
            </div>
          </motion.div>
        </section>
      </div>
    </div>
  );
}
